import {
  spikeConfigSchema,
  spikeReceivedListSchema,
  spikeStatusSchema,
  type SpikeConfig,
} from '@personal-os/shared';
import { z } from 'zod';

export class SpikeApiError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'SpikeApiError';
    this.status = status;
  }
}

async function request(
  path: string,
  token: string | null,
  init: RequestInit = {},
): Promise<unknown> {
  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  if (init.body) headers['Content-Type'] = 'application/json';
  const res = await fetch(`/api/spike${path}`, { ...init, headers, cache: 'no-store' });
  const body: unknown = await res.json().catch(() => null);
  if (!res.ok) {
    const message =
      body && typeof body === 'object' && 'message' in body && typeof body.message === 'string'
        ? body.message
        : `The server answered ${res.status}.`;
    throw new SpikeApiError(res.status, message);
  }
  return body;
}

export async function fetchSpikeStatus() {
  return spikeStatusSchema.parse(await request('/status', null));
}

export async function fetchSpikeConfig(token: string): Promise<SpikeConfig> {
  return spikeConfigSchema.parse(await request('/config', token));
}

export async function fetchReceived(token: string) {
  return spikeReceivedListSchema.parse(await request('/received?limit=50', token));
}

export async function clearReceived(token: string): Promise<void> {
  await request('/received', token, { method: 'DELETE' });
}

export async function subscribePush(
  token: string,
  subscription: PushSubscriptionJSON,
): Promise<void> {
  await request('/push/subscribe', token, { method: 'POST', body: JSON.stringify(subscription) });
}

const testResultSchema = z.object({
  ok: z.boolean(),
  results: z
    .array(z.object({ ok: z.boolean(), status: z.number().nullable(), removed: z.boolean() }))
    .optional(),
  scheduledInSeconds: z.number().optional(),
});

export async function sendTestPush(token: string, delaySeconds: 0 | 30) {
  const body = await request('/push/test', token, {
    method: 'POST',
    body: JSON.stringify({ delaySeconds }),
  });
  return testResultSchema.parse(body);
}
